import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import {
  getDocs,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import { dbFeatureFlags } from "../firebase";
import { FeatureFlag, FeatureAccess, FeatureFlagContextValue } from "../types";
import { useUser } from "./UserContext";

const DEFAULT_FLAGS: Omit<FeatureFlag, "id">[] = [
  {
    name: "subscription",
    enabledProd: false,
    enabledTest: false,
    description: "Premium subscriptions and ad banners",
  },
  {
    name: "lifetime",
    enabledProd: false,
    enabledTest: true,
    description: "Lifetime one-time purchase option on subscribe modal",
  },
  {
    name: "referrals",
    enabledProd: false,
    enabledTest: true,
    description: "Refer & Earn referral program",
  },
  {
    name: "adaptive_plans",
    enabledProd: false,
    enabledTest: true,
    description:
      "Adaptive/personalised running plans that adjust based on performance",
  },
  {
    name: "virtual_pet",
    enabledProd: true,
    enabledTest: true,
    description: "Virtual pet companion that grows with your app activity",
  },
  {
    name: "books",
    enabledProd: true,
    enabledTest: true,
    description:
      "Book tracking — library, reading progress, stats, and suggestions",
  },
  {
    name: "affiliate_ads",
    enabledProd: false,
    enabledTest: true,
    description:
      "Contextual affiliate product recommendations on the home page",
  },
  {
    name: "emails",
    enabledProd: false,
    enabledTest: true,
    description:
      "Automated emails — welcome email on sign up, nudge emails for inactive users",
  },
  {
    name: "cycle_pro_panel",
    enabledProd: false,
    enabledTest: false,
    description:
      "Unified cycle panel on home page — merges period status, log prompt, ring, and pro predictions into one card",
  },
];

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

export const FeatureFlagProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const userContext = useUser();
  const userDetails = userContext?.userDetails ?? null;

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    try {
      setIsLoading(true);

      // Check localStorage cache first (5-minute TTL)
      const CACHE_KEY = "onyxlife_feature_flags";
      const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          if (
            Date.now() - timestamp < CACHE_TTL &&
            Array.isArray(data) &&
            data.length > 0
          ) {
            setFlags(data);
            setIsLoading(false);
            return;
          }
        } catch {
          /* invalid cache, refetch */
        }
      }

      const snapshot = await getDocs(dbFeatureFlags);

      const allDocs = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as (FeatureFlag & { enabled?: boolean })[];

      // Deduplicate: keep the first doc per flag name, delete extras from Firestore
      const seen = new Map<string, FeatureFlag & { enabled?: boolean }>();
      const duplicateIds: string[] = [];
      for (const flag of allDocs) {
        if (seen.has(flag.name)) {
          duplicateIds.push(flag.id);
        } else {
          seen.set(flag.name, flag);
        }
      }

      // Clean up duplicates in Firestore (fire-and-forget)
      if (duplicateIds.length > 0) {
        Promise.all(
          duplicateIds.map((id) => deleteDoc(doc(dbFeatureFlags, id))),
        ).catch((err) => console.error("Error cleaning duplicate flags:", err));
      }

      let loaded: FeatureFlag[] = [];
      for (const raw of Array.from(seen.values())) {
        // Migrate old schema: single `enabled` → `enabledProd` + `enabledTest`
        if (
          raw.enabledProd === undefined &&
          raw.enabledTest === undefined &&
          raw.enabled !== undefined
        ) {
          const migrated: FeatureFlag = {
            id: raw.id,
            name: raw.name,
            description: raw.description,
            enabledProd: raw.enabled,
            enabledTest: raw.enabled,
          };
          // Persist migration to Firestore (fire-and-forget)
          updateDoc(doc(dbFeatureFlags, raw.id), {
            enabledProd: raw.enabled,
            enabledTest: raw.enabled,
            enabled: null,
          }).catch((err) => console.error("Error migrating flag:", err));
          loaded.push(migrated);
        } else {
          loaded.push({
            id: raw.id,
            name: raw.name,
            description: raw.description,
            enabledProd: raw.enabledProd ?? false,
            enabledTest: raw.enabledTest ?? false,
          });
        }
      }

      // Remove stale Firestore flags no longer in DEFAULT_FLAGS
      const validNames = new Set(DEFAULT_FLAGS.map((df) => df.name));
      const staleFlags = loaded.filter((f) => !validNames.has(f.name));
      if (staleFlags.length > 0) {
        Promise.all(
          staleFlags.map((f) => deleteDoc(doc(dbFeatureFlags, f.id))),
        ).catch((err) => console.error("Error cleaning stale flags:", err));
        loaded = loaded.filter((f) => validNames.has(f.name));
      }

      // Add any missing default flags to Firestore
      const missingFlags = DEFAULT_FLAGS.filter(
        (df) => !loaded.some((l) => l.name === df.name),
      );
      for (const flag of missingFlags) {
        const docRef = await addDoc(dbFeatureFlags, flag);
        loaded.push({ ...flag, id: docRef.id });
      }

      setFlags(loaded);

      // Cache to localStorage
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ data: loaded, timestamp: Date.now() }),
        );
      } catch {
        /* quota exceeded or private browsing */
      }
    } catch (error) {
      console.error("Error loading feature flags:", error);
      // Fallback: treat all features as enabled
      setFlags(DEFAULT_FLAGS.map((f, i) => ({ ...f, id: `fallback-${i}` })));
    } finally {
      setIsLoading(false);
    }
  };

  const flagMap = useMemo(() => {
    const map = new Map<string, FeatureFlag>();
    for (const f of flags) {
      map.set(f.name, f);
    }
    return map;
  }, [flags]);

  const defaultFlagMap = useMemo(() => {
    const map = new Map<string, Omit<FeatureFlag, "id">>();
    for (const df of DEFAULT_FLAGS) {
      map.set(df.name, df);
    }
    return map;
  }, []);

  const isFeatureEnabled = useCallback(
    (name: string): boolean => {
      // Testers get ALL features enabled regardless of flag state
      if (userDetails?.tester || userDetails?.admin) return true;

      const flag = flagMap.get(name);
      const defaultFlag = defaultFlagMap.get(name);
      const prod = flag ? flag.enabledProd : defaultFlag?.enabledProd;
      const test = flag ? flag.enabledTest : defaultFlag?.enabledTest;

      // If enabled for production, everyone sees it
      if (prod) return true;

      // If enabled for testing, on localhost everyone sees it
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      if (test && isLocalhost) return true;

      // Unknown flags default to true
      if (!flag && !defaultFlag) return true;

      return false;
    },
    [flagMap, defaultFlagMap, userDetails?.admin, userDetails?.tester],
  );

  const getFeatureAccess = useCallback(
    (name: string): FeatureAccess => {
      // Testers/admins always have access — return "test" for non-prod flags
      if (userDetails?.tester || userDetails?.admin) {
        const flag = flagMap.get(name);
        const defaultFlag = defaultFlagMap.get(name);
        const prod = flag ? flag.enabledProd : defaultFlag?.enabledProd;
        return prod ? "prod" : "test";
      }

      const flag = flagMap.get(name);
      const defaultFlag = defaultFlagMap.get(name);
      const prod = flag ? flag.enabledProd : defaultFlag?.enabledProd;
      const test = flag ? flag.enabledTest : defaultFlag?.enabledTest;

      if (prod) return "prod";

      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      if (test && isLocalhost) return "test";

      if (!flag && !defaultFlag) return "prod";

      return "disabled";
    },
    [flagMap, defaultFlagMap, userDetails?.admin, userDetails?.tester],
  );

  const toggleFlag = async (
    id: string,
    field: "enabledProd" | "enabledTest",
    value: boolean,
  ): Promise<void> => {
    try {
      const flagRef = doc(dbFeatureFlags, id);
      // Use setDoc with merge to avoid "No document to update" errors when the
      // doc ID was recycled by dedup/stale cleanup but the old ID is still in state.
      await setDoc(flagRef, { [field]: value }, { merge: true });
      setFlags((prev) => {
        const updated = prev.map((f) =>
          f.id === id ? { ...f, [field]: value } : f,
        );
        // Update localStorage cache so the toggle survives page refresh
        try {
          localStorage.setItem(
            "onyxlife_feature_flags",
            JSON.stringify({ data: updated, timestamp: Date.now() }),
          );
        } catch {
          /* quota exceeded or private browsing */
        }
        return updated;
      });
    } catch (error) {
      console.error("Error toggling feature flag:", error);
      throw error;
    }
  };

  const contextValue = useMemo(
    () => ({
      flags,
      isFeatureEnabled,
      getFeatureAccess,
      toggleFlag,
      isLoading,
    }),
    [flags, isFeatureEnabled, getFeatureAccess, toggleFlag, isLoading],
  );

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagContextValue => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within FeatureFlagProvider");
  }
  return context;
};
